/**
 * Parses WEBVTT-style consultation transcription (e.g. from Daily) into
 * a list of speaker lines for readable display.
 */
export type TranscriptLine = {
  speaker: string;
  text: string;
  /** Optional timestamp string for display, e.g. "00:01:23" */
  time?: string;
};

const SPEAKER_TAG_REGEX = /<v>([^<]*)<\/v>\s*(.*)/;

/**
 * Normalize speaker name for display (e.g. "Prince (Medpharma)" → "Prince", "guest" → "Guest").
 */
function formatSpeakerName(raw: string): string {
  const trimmed = raw.replace(/\s*:\s*$/, "").trim();
  // Remove parenthetical suffix like "(Medpharma)" for cleaner display
  const withoutSuffix = trimmed.replace(/\s*\([^)]*\)\s*$/, "").trim() || trimmed;
  if (!withoutSuffix) return trimmed;
  return withoutSuffix.charAt(0).toUpperCase() + withoutSuffix.slice(1).toLowerCase();
}

/**
 * Parse a timestamp line "00:00:01.901 --> 00:00:02.491" to a short form "0:01".
 */
function parseTimestampLine(line: string): string | undefined {
  const match = line.trim().match(/^(\d{2}):(\d{2}):(\d{2})/);
  if (!match) return undefined;
  const [, h, m, s] = match;
  const totalSeconds = parseInt(h!, 10) * 3600 + parseInt(m!, 10) * 60 + parseInt(s!, 10);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Parse raw WEBVTT/transcript string into an array of { speaker, text, time? }.
 */
export function parseMeetingTranscript(raw: string): TranscriptLine[] {
  if (!raw?.trim()) return [];

  const lines = raw.split(/\r?\n/);
  const result: TranscriptLine[] = [];
  let lastTimestamp: string | undefined;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Timestamp line (e.g. "00:00:01.901 --> 00:00:02.491")
    const time = parseTimestampLine(line);
    if (time) {
      lastTimestamp = time;
      continue;
    }

    // Speaker line: <v>Name:</v>Text
    const match = trimmed.match(SPEAKER_TAG_REGEX);
    if (match) {
      const speaker = formatSpeakerName(match[1]);
      const text = (match[2] ?? "").trim();
      if (speaker || text) {
        result.push({
          speaker: speaker || "Speaker",
          text,
          time: lastTimestamp,
        });
      }
      continue;
    }

    // Skip WEBVTT header, "transcript:N", and empty lines
    if (
      trimmed === "WEBVTT" ||
      /^transcript:\d+$/i.test(trimmed) ||
      trimmed === ""
    ) {
      continue;
    }

    // Continuation: line without a tag - append to last entry if any
    if (trimmed && result.length > 0) {
      result[result.length - 1].text += " " + trimmed;
    }
  }

  return result;
}
